// routes.js
import Dashboard from "layouts/dashboard";
import Tables from "layouts/tables";
import Billing from "layouts/billing";
import Notifications from "layouts/notifications";
import Profile from "layouts/profile";
import SignIn from "layouts/authentication/sign-in";
import SignUp from "layouts/authentication/sign-up";
import Register from "layouts/authentication/sign-up/register";
import Tithes from "layouts/accounts/tithes";
import Offerings from "layouts/accounts/offerings";
import Member from "layouts/members";
import SMS from "layouts/sms";
import DepartmentMembers from "layouts/department-members";
import Department from "layouts/departments";
import Attendance from "layouts/attendance";
import Biometric from "layouts/biometric";
import User from "layouts/users";
import Welfare from "layouts/accounts/welfare";
import MinistryMember from "layouts/ministry-member"; // Make sure this exists
import Ministry from "layouts/ministries";
// import Reports from "layouts/reports";
import MemberReport from "layouts/reports/member";
import WelfareReport from "layouts/reports/welfare";
import OfferingReport from "layouts/reports/offering";
import TitheReport from "layouts/reports/tithe";
import HarvestReport from "layouts/reports/harvest"; // Ensure this is the correct import
import AttendanceReport from "layouts/reports/attendance";
import SingleMember from "layouts/members/single-member";
import SingleTithe from "layouts/accounts/tithes/single-tithe";
import SingleWelfare from "layouts/accounts/welfare/single-welfare";
import MemberRegistrationPage from "layouts/members/MemberRegistrationPage";
import Branches from "layouts/branches";
import BranchDetail from "layouts/branch-detail";
import AuditLogs from "layouts/audit-logs";
import Expenses from "layouts/expenses";
import Projects from "layouts/projects";
import Contributions from "layouts/accounts/contributions";
import Events from "layouts/events";
import Visitors from "layouts/visitors";
import NewConverts from "layouts/new-converts";
import SmallGroups from "layouts/small-groups";
import PastoralCare from "layouts/pastoral-care";
import SacramentalRecords from "layouts/sacramental-records";
import ServingTeams from "layouts/serving-teams";
import QrCodes from "layouts/qr-codes";
import QrCheckIn from "layouts/qr-checkin";
import RegistrationQrCode from "layouts/registration-qr";
import SuperAdminDashboard from "layouts/super-admin";
import Payroll from "layouts/payroll";
import GroupDues from "layouts/group-dues";
import ChildCheckIn from "layouts/child-checkin";
import BulkEmail from "layouts/bulk-email";
import AccountingReport from "layouts/reports/accounting";
import IncomeStatement from "layouts/reports/income-statement";
import ActivityReportCenter from "layouts/reports/activity-center";
import Settings from "layouts/settings";
import Birthdays from "layouts/birthdays";
import WhatsAppMessages from "layouts/whatsapp";
import MyGroup from "layouts/my-group";
import Testimonies from "layouts/testimonies";
import Announcements from "layouts/announcements";

// @mui icons
import Icon from "@mui/material/Icon";
import { API_BASE, API_HOST } from "config/api";

// Initialize with empty department routes
let departmentRoutes = [];
let ministryRoutes = [];

export const fetchDepartments = async (churchId) => {
  try {
    const response = await fetch(`${API_BASE}/all-departments/${churchId}`);
    if (!response.ok) throw new Error("Network response was not ok");
    const json = await response.json();
    // Tolerate either a flat array or a {status, data} envelope.
    return Array.isArray(json) ? json : json?.data || [];
  } catch (error) {
    console.error("Error fetching departments:", error);
    return [];
  }
};

export const loadDepartmentRoutes = async (churchId) => {
  try {
    const departments = await fetchDepartments(churchId);

    departmentRoutes = departments.map((dept) => ({
      name: dept.name,
      key: `department-${dept.id}`,
      icon: <Icon fontSize="small">groups</Icon>,
      route: `/departments/${dept.id}`,
      component: <DepartmentMembers departmentId={dept.id} />,
      protected: true,
      roles: ["admin", "staff"], // Match parent route roles
    }));

    return departmentRoutes;
  } catch (error) {
    console.error("Failed to load department routes:", error);
    return [];
  }
};

// Ministry functions
export const fetchMinistries = async (churchId) => {
  try {
    const response = await fetch(`${API_BASE}/all-ministries/${churchId}`, {
      method: "GET",
      headers: { Accept: "application/json" },
    });
    if (!response.ok) throw new Error("Network response was not ok");
    const json = await response.json();
    return Array.isArray(json) ? json : json?.data || [];
  } catch (error) {
    console.error("Error fetching ministries:", error);
    return [];
  }
};

export const loadMinistryRoutes = async (churchId) => {
  try {
    const ministries = await fetchMinistries(churchId);

    ministryRoutes = ministries.map((min) => ({
      name: min.name,
      key: `ministry-${min.id}`,
      icon: <Icon fontSize="small">groups</Icon>,
      route: `/ministries/${min.id}`,
      component: <MinistryMember ministryId={min.id} />,
      protected: true,
      roles: ["admin", "staff"],
    }));

    return ministryRoutes;
  } catch (error) {
    console.error("Failed to load ministry routes:", error);
    return [];
  }
};

// Base routes configuration
export const baseRoutes = [
  {
    type: "collapse",
    name: "Dashboard",
    key: "dashboard",
    icon: <Icon fontSize="small">dashboard</Icon>,
    route: "/dashboard",
    component: <Dashboard />,
    protected: true,
    // roles: ["admin"],
  },
  {
    type: "collapse",
    name: "Calendar",
    key: "calendar",
    icon: <Icon fontSize="small">event</Icon>,
    route: "/calendar",
    component: <Events />,
    protected: true,
    roles: ["admin", "staff", "treasurer", "department_head", "ministry_head", "branch_admin"],
  },
  {
    type: "collapse",
    name: "Members",
    key: "members",
    icon: <Icon fontSize="small">group</Icon>,
    route: "/members",
    component: <Member />,
    protected: true,
    roles: ["admin", "staff", "branch_admin"],
  },
  {
    type: "collapse",
    name: "Visitors",
    key: "visitors",
    icon: <Icon fontSize="small">emoji_people</Icon>,
    route: "/visitors",
    component: <Visitors />,
    protected: true,
    roles: ["admin", "staff", "branch_admin"],
  },
  {
    type: "collapse",
    name: "New Converts",
    key: "new-converts",
    icon: <Icon fontSize="small">auto_awesome</Icon>,
    route: "/new-converts",
    component: <NewConverts />,
    protected: true,
    roles: ["admin", "staff", "branch_admin"],
  },
  {
    type: "collapse",
    name: "Small Groups",
    key: "small-groups",
    icon: <Icon fontSize="small">diversity_2</Icon>,
    route: "/small-groups",
    component: <SmallGroups />,
    protected: true,
    roles: ["admin", "staff", "branch_admin"],
  },
  {
    type: "collapse",
    name: "Pastoral Care",
    key: "pastoral-care",
    icon: <Icon fontSize="small">volunteer_activism</Icon>,
    route: "/pastoral-care",
    component: <PastoralCare />,
    protected: true,
    roles: ["admin", "staff", "pastor", "branch_admin"],
  },
  {
    type: "collapse",
    name: "Birthdays",
    key: "birthdays",
    icon: <Icon fontSize="small">cake</Icon>,
    route: "/birthdays",
    component: <Birthdays />,
    protected: true,
    roles: ["admin", "staff", "pastor", "branch_admin"],
  },
  {
    type: "collapse",
    name: "Serving Teams",
    key: "serving-teams",
    icon: <Icon fontSize="small">assignment_ind</Icon>,
    route: "/serving-teams",
    component: <ServingTeams />,
    protected: true,
    roles: ["admin", "staff", "branch_admin"],
  },
  {
    type: "collapse",
    name: "QR Codes",
    key: "qr-codes",
    icon: <Icon fontSize="small">qr_code_2</Icon>,
    route: "/qr-codes",
    component: <QrCodes />,
    protected: true,
    roles: ["admin", "staff", "branch_admin"],
  },
  {
    type: "collapse",
    name: "QR Check-In",
    key: "qr-checkin",
    icon: <Icon fontSize="small">qr_code_scanner</Icon>,
    route: "/qr-checkin",
    component: <QrCheckIn />,
    protected: true,
    roles: ["admin", "staff", "branch_admin"],
  },
  {
    type: "collapse",
    name: "Registration QR",
    key: "registration-qr",
    icon: <Icon fontSize="small">person_add</Icon>,
    route: "/registration-qr",
    component: <RegistrationQrCode />,
    protected: true,
    roles: ["admin", "staff", "pastor", "branch_admin"],
  },
  {
    type: "collapse",
    name: "Super Admin",
    key: "super-admin",
    icon: <Icon fontSize="small">admin_panel_settings</Icon>,
    route: "/super-admin",
    component: <SuperAdminDashboard />,
    protected: true,
    roles: ["super_admin"],
  },
  {
    type: "collapse",
    name: "Child Check-In",
    key: "child-checkin",
    icon: <Icon fontSize="small">child_care</Icon>,
    route: "/child-checkin",
    component: <ChildCheckIn />,
    protected: true,
    roles: ["admin", "staff", "branch_admin"],
  },
  {
    type: "collapse",
    name: "Bulk Email",
    key: "bulk-email",
    icon: <Icon fontSize="small">mail</Icon>,
    route: "/bulk-email",
    component: <BulkEmail />,
    protected: true,
    roles: ["admin", "staff", "branch_admin"],
  },
  {
    type: "collapse",
    name: "Sacramental Records",
    key: "sacramental-records",
    icon: <Icon fontSize="small">church</Icon>,
    route: "/sacramental-records",
    component: <SacramentalRecords />,
    protected: true,
    roles: ["admin", "staff", "pastor", "branch_admin"],
  },
  {
    type: "collapse",
    name: "Member",
    key: "member",
    icon: <Icon fontSize="small">group</Icon>,
    route: "/member",
    component: <SingleMember />,
    protected: true,
    roles: ["member", "ministry_head", "department_head"],
  },
  {
    type: "collapse",
    name: "My Group",
    key: "my-group",
    icon: <Icon fontSize="small">groups</Icon>,
    route: "/my-group",
    component: <MyGroup />,
    protected: true,
    roles: ["member", "ministry_head", "department_head"],
  },
  {
    type: "collapse",
    name: "Testimonies",
    key: "testimonies",
    icon: <Icon fontSize="small">favorite</Icon>,
    route: "/testimonies",
    component: <Testimonies />,
    protected: true,
    roles: [
      "member",
      "ministry_head",
      "department_head",
      "pastor",
      "admin",
      "staff",
      "branch_admin",
    ],
  },
  {
    type: "collapse",
    name: "Announcements",
    key: "announcements",
    icon: <Icon fontSize="small">campaign</Icon>,
    route: "/announcements",
    component: <Announcements />,
    protected: true,
    roles: [
      "member",
      "ministry_head",
      "department_head",
      "pastor",
      "admin",
      "staff",
      "branch_admin",
      "treasurer",
    ],
  },
  {
    type: "collapse",
    name: "Add Departments",
    key: "all-departments",
    icon: <Icon fontSize="small">group_add</Icon>,
    route: "/all-departments",
    component: <Department />,
    protected: true,
    roles: ["admin"],
  },
  {
    type: "collapse",
    name: "Church Departments",
    key: "departments",
    icon: <Icon fontSize="small">category</Icon>,
    route: "/departments", // Add a base route
    component: <DepartmentMembers />, // Main departments component
    protected: true,
    roles: ["admin", "staff", "branch_admin"],
    collapse: [], // Will be populated dynamically
  },
  {
    type: "collapse",
    name: "Add Ministries",
    key: "all-ministries",
    icon: <Icon fontSize="small">group_add</Icon>,
    route: "/all-ministries",
    component: <Ministry />,
    protected: true,
    roles: ["admin"],
  },
  {
    type: "collapse",
    name: "Ministries",
    key: "ministries",
    icon: <Icon fontSize="small">diversity_3</Icon>,
    route: "/ministries",
    component: <MinistryMember />,
    protected: true,
    roles: ["admin", "staff", "branch_admin"],
    collapse: [], // Will be populated dynamically
  },
  {
    type: "collapse",
    name: "Branches",
    key: "branches",
    icon: <Icon fontSize="small">store</Icon>,
    route: "/branches",
    component: <Branches />,
    protected: true,
    roles: ["admin"],
  },
  {
    type: "collapse",
    name: "Audit Log",
    key: "audit-logs",
    icon: <Icon fontSize="small">history</Icon>,
    route: "/audit-logs",
    component: <AuditLogs />,
    protected: true,
    roles: ["admin", "branch_admin"],
  },
  {
    type: "hidden",
    name: "Branch Detail",
    key: "branch-detail",
    route: "/branches/:branchId",
    component: <BranchDetail />,
    protected: true,
    roles: ["admin"],
  },
  {
    type: "collapse",
    name: "Attendance",
    key: "attendance",
    icon: <Icon fontSize="small">how_to_reg</Icon>,
    route: "/attendance",
    component: <Attendance />,
    protected: true,
    roles: ["admin", "staff", "branch_admin"],
  },
  {
    type: "hidden",
    key: "attendance-device",
    route: "/attendance/device",
    component: <Attendance />,
    protected: true,
    roles: ["admin", "staff", "branch_admin"],
  },
  {
    type: "collapse",
    name: "Biometric",
    key: "biometric",
    icon: <Icon fontSize="small">fingerprint</Icon>,
    route: "/biometric",
    component: <Biometric />,
    protected: true,
    roles: ["admin", "branch_admin"],
  },
  {
    type: "collapse",
    name: "Tithe",
    key: "tithe",
    icon: <Icon fontSize="small">payments</Icon>,
    route: "/tithe",
    component: <SingleTithe />,
    protected: true,
    roles: ["member", "ministry_head", "department_head"],
  },
  {
    type: "collapse",
    name: "Welfare",
    key: "welfare",
    icon: <Icon fontSize="small">payments</Icon>,
    route: "/welfare",
    component: <SingleWelfare />,
    protected: true,
    roles: ["member", "ministry_head", "department_head"],
  },
  {
    type: "collapse",
    name: "Accounts",
    key: "accounts",
    icon: <Icon fontSize="small">attach_money</Icon>,
    collapse: [
      {
        name: "Tithes",
        key: "tithes",
        icon: <Icon fontSize="small">payments</Icon>,
        route: "/accounts/tithes",
        component: <Tithes />,
        protected: true,
        roles: ["admin", "treasurer", "branch_admin"],
      },
      {
        name: "Welfare",
        key: "welfare",
        icon: <Icon fontSize="small">payments</Icon>,
        route: "/accounts/welfare",
        component: <Welfare />,
        protected: true,
        roles: ["admin", "treasurer", "branch_admin"],
      },
      {
        name: "Offerings",
        key: "offerings",
        icon: <Icon fontSize="small">payments</Icon>,
        route: "/accounts/offerings",
        component: <Offerings />,
        protected: true,
        roles: ["admin", "treasurer", "branch_admin"],
      },
      {
        name: "Contributions",
        key: "contributions",
        icon: <Icon fontSize="small">volunteer_activism</Icon>,
        route: "/accounts/contributions",
        component: <Contributions />,
        protected: true,
        roles: ["admin", "treasurer", "branch_admin"],
      },
      {
        name: "Expenses",
        key: "expenses",
        icon: <Icon fontSize="small">receipt_long</Icon>,
        route: "/accounts/expenses",
        component: <Expenses />,
        protected: true,
        roles: ["admin", "treasurer", "branch_admin"],
      },
      {
        name: "Payroll",
        key: "payroll",
        icon: <Icon fontSize="small">payments</Icon>,
        route: "/accounts/payroll",
        component: <Payroll />,
        protected: true,
        roles: ["admin", "treasurer", "branch_admin"],
      },
      {
        name: "Projects",
        key: "projects",
        icon: <Icon fontSize="small">volunteer_activism</Icon>,
        route: "/accounts/projects",
        component: <Projects />,
        protected: true,
        roles: ["admin", "treasurer", "pastor", "department_head", "ministry_head", "branch_admin"],
      },
      {
        name: "Group Dues",
        key: "group-dues",
        icon: <Icon fontSize="small">receipt_long</Icon>,
        route: "/accounts/group-dues",
        component: <GroupDues />,
        protected: true,
        roles: ["admin", "treasurer", "pastor", "department_head", "ministry_head", "branch_admin"],
      },
    ],
    protected: true,
    roles: ["admin", "treasurer", "branch_admin"],
  },
  {
    type: "collapse",
    name: "Message",
    key: "sms",
    icon: <Icon fontSize="small">textsms</Icon>,
    route: "/sms",
    component: <SMS />,
    protected: true,
    roles: ["admin", "staff", "treasurer", "branch_admin"],
  },
  {
    type: "collapse",
    name: "WhatsApp",
    key: "whatsapp",
    icon: <Icon fontSize="small">chat</Icon>,
    route: "/whatsapp",
    component: <WhatsAppMessages />,
    protected: true,
    roles: ["admin", "staff", "pastor"],
  },
  {
    type: "collapse",
    name: "Reports",
    key: "reports",
    icon: <Icon fontSize="small">assessment</Icon>,
    collapse: [
      {
        name: "Member Report",
        key: "member-report",
        icon: <Icon fontSize="small">people</Icon>,
        route: "/reports/member",
        component: <MemberReport />,
        protected: true,
        roles: ["admin", "branch_admin"],
      },
      {
        name: "Attendance Report",
        key: "attendance-report",
        icon: <Icon fontSize="small">event_note</Icon>,
        route: "/reports/attendance",
        component: <AttendanceReport />,
        protected: true,
        roles: ["admin", "branch_admin"],
      },
      // {
      //   name: "Welfare Report",
      //   key: "welfare-report",
      //   icon: <Icon fontSize="small">payments</Icon>,
      //   route: "/reports/welfare",
      //   component: <WelfareReport />,
      //   protected: true,
      //   roles: ["admin", "member"],
      // },
      // {
      //   name: "Offering Report",
      //   key: "offering-report",
      //   icon: <Icon fontSize="small">payments</Icon>,
      //   route: "/reports/offerings",
      //   component: <OfferingReport />,
      //   protected: true,
      //   roles: ["admin"],
      // },
      // {
      //   name: "Tithe Report",
      //   key: "tithe-report",
      //   icon: <Icon fontSize="small">payments</Icon>,
      //   route: "/reports/tithe",
      //   component: <TitheReport />,
      //   protected: true,
      //   roles: ["admin"],
      // },
      {
        name: "Welfare",
        key: "welfare",
        icon: <Icon fontSize="small">payments</Icon>,
        route: "/reports/welfare",
        component: <WelfareReport />,
        protected: true,
        roles: ["admin", "treasurer", "branch_admin"],
      },
      {
        name: "Offerings",
        key: "offerings",
        icon: <Icon fontSize="small">payments</Icon>,
        route: "/reports/offerings",
        component: <OfferingReport />,
        protected: true,
        roles: ["admin", "treasurer", "branch_admin"],
      },
      {
        name: "Tithes",
        key: "tithes",
        icon: <Icon fontSize="small">payments</Icon>,
        route: "/reports/tithe",
        component: <TitheReport />,
        protected: true,
        roles: ["admin", "treasurer", "branch_admin"],
      },
      {
        name: "Harvests",
        key: "harvests",
        icon: <Icon fontSize="small">payments</Icon>,
        route: "/reports/harvest",
        component: <HarvestReport />,
        protected: true,
        roles: ["admin", "treasurer", "branch_admin"],
      },
      {
        name: "Accounting",
        key: "accounting-report",
        icon: <Icon fontSize="small">account_balance</Icon>,
        route: "/reports/accounting",
        component: <AccountingReport />,
        protected: true,
        roles: ["admin", "treasurer", "branch_admin"],
      },
      {
        name: "Income Statement",
        key: "income-statement",
        icon: <Icon fontSize="small">summarize</Icon>,
        route: "/reports/income-statement",
        component: <IncomeStatement />,
        protected: true,
        roles: ["admin", "treasurer", "branch_admin"],
      },
      {
        name: "Activity Report Center",
        key: "activity-report-center",
        icon: <Icon fontSize="small">dashboard</Icon>,
        route: "/reports/activity-center",
        component: <ActivityReportCenter />,
        protected: true,
        roles: ["admin", "pastor", "branch_admin"],
      },
    ],
    protected: true,
    roles: ["admin", "branch_admin"],
  },
  // {
  //   type: "collapse",
  //   name: "Reports",
  //   key: "billing",
  //   icon: <Icon fontSize="small">assessment</Icon>,
  //   route: "/billing",
  //   component: <Billing />,
  //   protected: true,
  //   roles: ["admin"],
  // },
  {
    type: "collapse",
    name: "Users Account",
    key: "user",
    icon: <Icon fontSize="small">lock</Icon>,
    route: "/user",
    component: <User />,
    protected: true,
    roles: ["admin", "branch_admin"],
  },
  {
    type: "collapse",
    name: "Profile",
    key: "profile",
    icon: <Icon fontSize="small">person</Icon>,
    route: "/profile",
    component: <Profile />,
    protected: true,
    roles: ["admin", "branch_admin"],
  },
  {
    type: "collapse",
    name: "Settings",
    key: "settings",
    icon: <Icon fontSize="small">settings</Icon>,
    route: "/settings",
    component: <Settings />,
    protected: true,
    roles: ["admin", "branch_admin"],
  },
  {
    type: "hidden",
    name: "Member Registration",
    key: "member-registration",
    icon: <Icon fontSize="small">person_add</Icon>,
    route: "/member-registration/:churchId",
    component: <MemberRegistrationPage />,
    protected: false, // No authentication required - public access
  },
  {
    type: "hidden",
    name: "Sign In",
    key: "sign-in",
    icon: <Icon fontSize="small">login</Icon>,
    route: "/authentication/sign-in",
    component: <SignIn />,
    protected: false,
  },
  {
    type: "hidden",
    name: "Sign Up",
    key: "sign-up",
    icon: <Icon fontSize="small">assignment</Icon>,
    route: "/authentication/sign-up",
    component: <SignUp />,
  },
  {
    type: "hidden",
    name: "Register",
    key: "register",
    route: "/authentication/register/:churchId/:churchName",
    component: <Register />,
  },
];

// Function to get current routes with latest department data
export const getRoutes = () => {
  return baseRoutes.map((route) => {
    if (route.key === "departments") {
      return {
        ...route,
        collapse:
          departmentRoutes.length > 0
            ? departmentRoutes
            : [
                {
                  name: "No Departments",
                  key: "no-departments",
                  icon: <Icon fontSize="small">info</Icon>,
                  route: "#",
                  component: <div>No departments available</div>,
                  protected: true,
                },
              ],
      };
    }
    if (route.key === "ministries") {
      return {
        ...route,
        collapse:
          ministryRoutes.length > 0
            ? ministryRoutes
            : [
                {
                  name: "No Ministries",
                  key: "no-ministries",
                  icon: <Icon fontSize="small">info</Icon>,
                  route: "#",
                  component: <div>No ministries available</div>,
                  protected: true,
                },
              ],
      };
    }
    return route;
  });
};

// Filter out hidden routes for sidenav
export const getSidenavRoutes = () => {
  return getRoutes()
    .filter((route) => route.type !== "hidden")
    .map((route) => {
      if (route.collapse) {
        return {
          ...route,
          collapse: route.collapse.map((item) => ({
            ...item,
            component: item.component || null,
          })),
        };
      }
      return route;
    });
};
